import { type ReactNode, useState } from 'react'
import {
  type Connectivity,
  type NetstatusParams,
  type StatusCard,
  useDashboardNetwork,
  useDashboardPos,
  useNetstatus,
} from '../../../features/dashboard/use-dashboard'
import { Chip, Empty, td, th, utc } from './controls'
import { Dot, mono, Note, Panel, SLink } from './ui'

type CardStatus = 'online' | 'degraded' | 'offline'
const CARD_ORDER: CardStatus[] = ['online', 'degraded', 'offline']
const TONE = { online: 'ok', degraded: 'warn', offline: 'bad', unknown: 'off' } as const
const VAR = { online: 'var(--v2-ok)', degraded: 'var(--v2-warn)', offline: 'var(--v2-bad)' } as const
const NET_RANGES = ['1d', '7d', '30d']
const POS_RANGES = ['1d', '3d', '7d', '30d']
const WAN_SLOTS: [string, string][] = [
  ['', 'WAN'],
  ['wan1', 'WAN1'],
  ['wan2', 'WAN2'],
]
const SKEL_ROWS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h']
const LABEL = 'text-[11px] uppercase tracking-[0.06em] text-[var(--color-muted)]'
const SELECT =
  'h-6 rounded-[4px] border border-[var(--color-border)] bg-[var(--color-bg)] px-1 text-[12px] focus:border-[var(--color-accent)] focus:outline-none'

function Range({
  value,
  options,
  onChange,
  label,
}: {
  value: string
  options: string[]
  onChange: (v: string) => void
  label: string
}) {
  return (
    <div title={label} className="inline-flex overflow-hidden rounded-[4px] border border-[var(--color-border)]">
      {options.map((o) => {
        const on = o === value
        return (
          <button
            key={o}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o)}
            className="h-5 px-1.5 text-[11px] focus-visible:outline-1 focus-visible:outline-[var(--color-accent)]"
            style={{
              ...mono,
              background: on ? 'var(--v2-accent-soft)' : undefined,
              color: on ? 'var(--color-accent)' : 'var(--color-muted)',
            }}
          >
            {o}
          </button>
        )
      })}
    </div>
  )
}

function Skel({ w = 'w-full' }: { w?: string }) {
  return <span className={`block h-2 ${w} animate-pulse rounded-[2px] bg-[var(--color-surface-2)]`} />
}

function PanelSkel({ rows = 3 }: { rows?: number }) {
  return (
    <div className="grid gap-2.5 p-3" aria-busy="true">
      {SKEL_ROWS.slice(0, rows).map((k, i) => (
        <Skel key={k} w={i % 2 ? 'w-2/3' : 'w-full'} />
      ))}
    </div>
  )
}

function Metric({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: CardStatus }) {
  return (
    <div className="min-w-0 border-r border-[var(--color-border)] px-3 py-2 last:border-r-0">
      <div className={`truncate ${LABEL}`}>{label}</div>
      <div className="text-[20px] leading-7 font-medium" style={{ ...mono, color: tone ? VAR[tone] : undefined }}>
        {value}
      </div>
      {sub !== undefined && (
        <div className="truncate text-[11px] text-[var(--color-muted)]" style={mono}>
          {sub}
        </div>
      )}
    </div>
  )
}

function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return <span className="text-[11px] text-[var(--color-muted)]">no trend</span>
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const pts = values
    .map((v, i) => `${((i / (values.length - 1)) * 100).toFixed(1)},${(13 - ((v - min) / span) * 12).toFixed(1)}`)
    .join(' ')
  return (
    <svg viewBox="0 0 100 14" preserveAspectRatio="none" className="h-4 w-24" role="img" aria-label="uptime trend">
      <polyline
        points={pts}
        fill="none"
        stroke="var(--color-accent)"
        strokeWidth="1.25"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

/** Proportional bar with a direct label per segment (no legend). */
function Split({ parts }: { parts: { label: string; n: number; color: string }[] }) {
  const total = parts.reduce((s, p) => s + p.n, 0)
  return (
    <div className="px-3 py-2">
      <div
        className="flex h-1.5 gap-px overflow-hidden rounded-[2px] bg-[var(--color-surface-2)]"
        role="img"
        aria-label={parts.map((p) => `${p.label} ${p.n}`).join(', ')}
      >
        {total > 0 &&
          parts
            .filter((p) => p.n > 0)
            .map((p) => <span key={p.label} style={{ width: `${(p.n / total) * 100}%`, background: p.color }} />)}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-4 text-[11px] text-[var(--color-muted)]" style={mono}>
        {parts.map((p) => (
          <span key={p.label}>
            <span style={{ color: p.n > 0 ? p.color : undefined }}>{p.n}</span> {p.label}
          </span>
        ))}
      </div>
    </div>
  )
}

function Row({ k, v, tone }: { k: string; v: ReactNode; tone?: CardStatus }) {
  return (
    <div className="flex h-7 items-center justify-between gap-3 border-b border-[var(--color-border)] px-3 text-[12px]">
      <span className="text-[var(--color-muted)]">{k}</span>
      <span style={{ ...mono, color: tone ? VAR[tone] : undefined }}>{v}</span>
    </div>
  )
}

function CardRow({
  title,
  cards,
  active,
  onPick,
}: {
  title: string
  cards: StatusCard[]
  active?: CardStatus
  onPick: (s: CardStatus | undefined) => void
}) {
  const by = new Map(cards.map((c) => [c.status, c]))
  return (
    <div className="grid grid-cols-[56px_repeat(3,minmax(0,1fr))] items-center border-b border-[var(--color-border)] last:border-b-0">
      <span className={`pl-3 ${LABEL}`}>{title}</span>
      {CARD_ORDER.map((s) => {
        const c = by.get(s)
        const on = active === s
        return (
          <button
            key={s}
            type="button"
            aria-pressed={on}
            onClick={() => onPick(on ? undefined : s)}
            className="flex h-9 items-center gap-2 border-l border-[var(--color-border)] px-3 text-left hover:bg-[var(--color-surface-2)] focus-visible:outline-1 focus-visible:outline-[var(--color-accent)]"
            style={{
              background: on ? 'var(--v2-accent-soft)' : undefined,
              boxShadow: on ? 'inset 2px 0 0 var(--color-accent)' : undefined,
            }}
          >
            <Dot tone={TONE[s]} />
            <span
              className="text-[16px] leading-none font-medium"
              style={{ ...mono, color: s === 'online' ? undefined : VAR[s] }}
            >
              {c?.count ?? 0}
            </span>
            <span className="truncate text-[11px] text-[var(--color-muted)]" style={mono}>
              {s} {c ? `${c.pct.toFixed(0)}%` : ''}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function Conn({ s }: { s: Connectivity }) {
  const quiet = s === 'online' || s === 'unknown'
  return (
    <span
      className="inline-flex items-center gap-1.5"
      style={{ color: quiet ? 'var(--color-muted)' : s === 'degraded' ? VAR.degraded : VAR.offline }}
    >
      <Dot tone={TONE[s]} />
      {s}
    </span>
  )
}

function PageBtn({ children, disabled, onClick }: { children: ReactNode; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="h-5 rounded-[3px] border border-[var(--color-border)] px-1.5 hover:bg-[var(--color-surface-2)] disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  )
}

const loc = (code: string) => `/locations/${encodeURIComponent(code)}`

export function SignalDashboard() {
  const [netRange, setNetRange] = useState('30d')
  const [posRange, setPosRange] = useState('1d')
  const [params, setParams] = useState<NetstatusParams>({})
  const [searchDraft, setSearchDraft] = useState('')

  const ns = useNetstatus(params)
  const net = useDashboardNetwork(netRange)
  const pos = useDashboardPos(posRange)

  const patch = (p: Partial<NetstatusParams>) => setParams((cur) => ({ ...cur, page: 1, ...p }))
  const filtersOn = Boolean(params.status || params.wanStatus || params.search)
  const d = ns.data
  const lastPage = d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1
  const page = d?.page ?? 1
  const offline = d?.cards.find((c) => c.status === 'offline')?.count ?? 0
  const degraded = d?.cards.find((c) => c.status === 'degraded')?.count ?? 0
  const attn = net.data?.attention ?? []

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-11 shrink-0 items-center gap-3 border-b border-[var(--color-border)] px-4">
        <h1 className="text-[14px] font-semibold tracking-[-0.01em]">Dashboard</h1>
        {d && (
          <span className="flex items-center gap-1.5 text-[12px] text-[var(--color-muted)]" style={mono}>
            <Dot tone={offline > 0 ? 'bad' : degraded > 0 ? 'warn' : 'ok'} />
            {d.total} locations · {offline} offline · {degraded} degraded
          </span>
        )}
        {d && (
          <span className="ml-auto text-[11px] text-[var(--color-muted)]" style={mono}>
            synced {utc(d.cachedAt)}
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div className="grid content-start gap-3">
            <Panel
              title="Connectivity"
              aside={
                filtersOn ? (
                  <button
                    type="button"
                    className="text-[var(--color-accent)] hover:underline"
                    onClick={() => {
                      setParams({})
                      setSearchDraft('')
                    }}
                  >
                    clear filters
                  </button>
                ) : (
                  <span className="text-[var(--color-muted)]">click to filter</span>
                )
              }
            >
              {ns.error ? (
                <Note error>Failed to load netstatus: {ns.error.message}</Note>
              ) : !d ? (
                <PanelSkel rows={4} />
              ) : (
                <div>
                  <CardRow title="Store" cards={d.cards} active={params.status} onPick={(s) => patch({ status: s })} />
                  <CardRow
                    title="WAN"
                    cards={d.wanCards}
                    active={params.wanInterface ? undefined : params.wanStatus}
                    onPick={(s) => patch({ wanStatus: s, wanInterface: undefined })}
                  />
                  <CardRow
                    title="WAN1"
                    cards={d.wan1Cards}
                    active={params.wanInterface === 'wan1' ? params.wanStatus : undefined}
                    onPick={(s) => patch({ wanStatus: s, wanInterface: s ? 'wan1' : undefined })}
                  />
                  <CardRow
                    title="WAN2"
                    cards={d.wan2Cards}
                    active={params.wanInterface === 'wan2' ? params.wanStatus : undefined}
                    onPick={(s) => patch({ wanStatus: s, wanInterface: s ? 'wan2' : undefined })}
                  />
                </div>
              )}
            </Panel>

            <Panel
              title="Locations"
              aside={
                d && (
                  <span className="text-[var(--color-muted)]" style={mono}>
                    {d.total
                      ? `${(page - 1) * d.pageSize + 1}-${Math.min(page * d.pageSize, d.total)} of ${d.total}`
                      : '0'}
                  </span>
                )
              }
            >
              <form
                className="flex h-8 items-center gap-2 border-b border-[var(--color-border)] px-3"
                onSubmit={(e) => {
                  e.preventDefault()
                  patch({ search: searchDraft.trim() || undefined })
                }}
              >
                <input
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Search code or name, Enter"
                  aria-label="Search locations"
                  spellCheck={false}
                  className="h-6 min-w-0 flex-1 rounded-[4px] border border-[var(--color-border)] bg-[var(--color-bg)] px-2 text-[12px] placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none"
                />
                <select
                  aria-label="WAN slot"
                  value={params.wanInterface ?? ''}
                  onChange={(e) =>
                    patch({ wanInterface: (e.target.value || undefined) as NetstatusParams['wanInterface'] })
                  }
                  className={SELECT}
                >
                  {WAN_SLOTS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="WAN status"
                  value={params.wanStatus ?? ''}
                  onChange={(e) => patch({ wanStatus: (e.target.value || undefined) as NetstatusParams['wanStatus'] })}
                  className={SELECT}
                >
                  <option value="">any</option>
                  {CARD_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </form>
              {!ns.error && (
                <table
                  className="w-full table-fixed border-collapse text-left"
                  aria-label="Locations"
                  aria-busy={ns.isFetching}
                >
                  <colgroup>
                    <col style={{ width: 72 }} />
                    <col />
                    <col style={{ width: 44 }} />
                    <col style={{ width: 96 }} />
                    <col style={{ width: 96 }} />
                    <col style={{ width: 96 }} />
                  </colgroup>
                  <thead className="bg-[var(--color-surface)]">
                    <tr>
                      {['Code', 'Name', 'ST', 'Store', 'WAN1', 'WAN2'].map((h) => (
                        <th key={h} scope="col" className={th}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody style={{ opacity: ns.isFetching && d ? 0.6 : 1 }}>
                    {!d &&
                      SKEL_ROWS.map((k, i) => (
                        <tr key={k} className="h-7">
                          <td colSpan={6} className="border-b border-[var(--color-border)] px-3">
                            <Skel w={i % 2 ? 'w-3/4' : 'w-full'} />
                          </td>
                        </tr>
                      ))}
                    {d?.locations.map((l) => (
                      <tr key={l.code} className="h-7 hover:bg-[var(--color-surface-2)]">
                        <td className={td} style={mono}>
                          <SLink to={loc(l.code)} className="text-[var(--color-accent)] hover:underline">
                            {l.code}
                          </SLink>
                        </td>
                        <td className={td}>{l.name}</td>
                        <td className={`${td} text-[var(--color-muted)]`} style={mono}>
                          {l.state ?? '-'}
                        </td>
                        <td className={td}>
                          <Conn s={l.connectivity} />
                        </td>
                        <td className={td}>
                          <Conn s={l.wan1} />
                        </td>
                        <td className={td}>
                          <Conn s={l.wan2} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {d && d.locations.length === 0 && (
                <Empty>{filtersOn ? 'No locations match these filters.' : 'No locations.'}</Empty>
              )}
              {d && (
                <div
                  className="flex h-7 items-center justify-between border-t border-[var(--color-border)] px-3 text-[11px] text-[var(--color-muted)]"
                  style={mono}
                >
                  <span>
                    page {page} / {lastPage}
                  </span>
                  <span className="flex gap-1">
                    <PageBtn disabled={page <= 1} onClick={() => setParams((p) => ({ ...p, page: page - 1 }))}>
                      prev
                    </PageBtn>
                    <PageBtn disabled={page >= lastPage} onClick={() => setParams((p) => ({ ...p, page: page + 1 }))}>
                      next
                    </PageBtn>
                  </span>
                </div>
              )}
            </Panel>
          </div>

          <div className="grid content-start gap-3">
            <Panel
              title={`Attention${attn.length ? ` · ${attn.length}` : ''}`}
              aside={<Range label="Network range" value={netRange} options={NET_RANGES} onChange={setNetRange} />}
            >
              {net.error ? (
                <Note error>Failed to load network health: {net.error.message}</Note>
              ) : !net.data ? (
                <PanelSkel rows={5} />
              ) : attn.length === 0 ? (
                <p className="flex h-12 items-center gap-2 px-3 text-[12px] text-[var(--color-muted)]">
                  <Dot tone="ok" />
                  Nothing needs attention. No tunnels or devices flagged.
                </p>
              ) : (
                <ul className="max-h-[288px] overflow-auto">
                  {attn.map((a) => (
                    <li
                      key={`${a.storeCode}-${a.vendor}`}
                      className="flex h-8 items-center gap-2 border-b border-[var(--color-border)] px-3 text-[12px] last:border-b-0"
                      style={{ boxShadow: `inset 2px 0 0 ${a.status === 'down' ? VAR.offline : VAR.degraded}` }}
                    >
                      <Chip tone={a.status === 'down' ? 'bad' : 'warn'}>{a.status}</Chip>
                      <SLink to={loc(a.storeCode)} className="text-[var(--color-accent)] hover:underline">
                        <span style={mono}>{a.storeCode}</span>
                      </SLink>
                      <span className="min-w-0 flex-1 truncate">{a.storeName}</span>
                      <span className="shrink-0 text-[var(--color-muted)]" style={mono}>
                        {a.detail}
                      </span>
                      <Chip>{a.vendor === 'cloudflare' ? 'cf' : 'meraki'}</Chip>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title="Network health">
              {net.error ? (
                <Note error>Unavailable.</Note>
              ) : !net.data ? (
                <PanelSkel rows={3} />
              ) : (
                <div style={{ opacity: net.isFetching ? 0.6 : 1 }}>
                  <div className="grid grid-cols-3 border-b border-[var(--color-border)]">
                    <Metric
                      label="Tunnel uptime"
                      value={`${net.data.tunnelHealth.avgUptimePct.toFixed(1)}%`}
                      sub={`${net.data.tunnelHealth.trendDelta >= 0 ? '+' : ''}${net.data.tunnelHealth.trendDelta.toFixed(1)} vs prev`}
                    />
                    <Metric
                      label="Tunnels down"
                      value={net.data.tunnelHealth.currentlyDown}
                      tone={net.data.tunnelHealth.currentlyDown ? 'offline' : undefined}
                    />
                    <Metric
                      label="Device issues"
                      value={net.data.deviceHealth.issueCount}
                      sub={`${net.data.deviceHealth.unreachable} unreachable`}
                      tone={net.data.deviceHealth.issueCount ? 'degraded' : undefined}
                    />
                  </div>
                  <div className="flex h-8 items-center justify-between border-b border-[var(--color-border)] px-3">
                    <span className={LABEL}>Uptime trend ({net.data.range})</span>
                    <Sparkline values={net.data.tunnelHealth.sparkline} />
                  </div>
                  <div className="border-b border-[var(--color-border)]">
                    <div className={`px-3 pt-2 ${LABEL}`}>Tunnel mix ({net.data.tunnelMix.total})</div>
                    <Split
                      parts={[
                        {
                          label: `cloudflare ${net.data.tunnelMix.cloudflare.up}/${net.data.tunnelMix.cloudflare.total} up`,
                          n: net.data.tunnelMix.cloudflare.total,
                          color: 'var(--color-accent)',
                        },
                        {
                          label: `meraki ${net.data.tunnelMix.meraki.up}/${net.data.tunnelMix.meraki.total} up`,
                          n: net.data.tunnelMix.meraki.total,
                          color: 'var(--v2-off)',
                        },
                      ]}
                    />
                  </div>
                  <div className={`px-3 pt-2 ${LABEL}`}>WAN links ({net.data.wanLinks.locationsWithWan} sites)</div>
                  <Split
                    parts={[
                      { label: 'primary', n: net.data.wanLinks.primary, color: 'var(--color-accent)' },
                      { label: 'secondary', n: net.data.wanLinks.secondary, color: 'var(--v2-warn)' },
                      { label: 'tertiary', n: net.data.wanLinks.tertiary, color: 'var(--v2-bad)' },
                    ]}
                  />
                </div>
              )}
            </Panel>

            <Panel
              title="POS"
              aside={<Range label="POS range" value={posRange} options={POS_RANGES} onChange={setPosRange} />}
            >
              {pos.error ? (
                <Note error>Failed to load POS health: {pos.error.message}</Note>
              ) : !pos.data ? (
                <PanelSkel rows={4} />
              ) : (
                <div style={{ opacity: pos.isFetching ? 0.6 : 1 }}>
                  <div className="grid grid-cols-3 border-b border-[var(--color-border)]">
                    <Metric
                      label="Terminals"
                      value={pos.data.terminalStatus.total}
                      sub={`${pos.data.terminalStatus.online} online`}
                    />
                    <Metric
                      label="Offline"
                      value={pos.data.terminalStatus.offline}
                      tone={pos.data.terminalStatus.offline ? 'offline' : undefined}
                    />
                    <Metric
                      label="Stale check-ins"
                      value={pos.data.staleCheckIns.count}
                      sub={`${pos.data.staleCheckIns.affectedLocations} locations`}
                      tone={pos.data.staleCheckIns.count ? 'degraded' : undefined}
                    />
                  </div>
                  <Row
                    k={`Unused workstations (${pos.data.unusedWorkstations.days}d)`}
                    v={pos.data.unusedWorkstations.count}
                    tone={pos.data.unusedWorkstations.count ? 'degraded' : undefined}
                  />
                  <div className="border-b border-[var(--color-border)]">
                    <div className={`px-3 pt-2 ${LABEL}`}>
                      Payment mix ({pos.data.paymentMethods.locationsWithAdyen} Adyen sites)
                    </div>
                    <Split
                      parts={[
                        { label: 'active', n: pos.data.paymentMethods.active, color: 'var(--v2-ok)' },
                        { label: 'inactive', n: pos.data.paymentMethods.inactive, color: 'var(--v2-off)' },
                      ]}
                    />
                  </div>
                  <div className={`flex h-6 items-center px-3 ${LABEL}`}>
                    Failing terminals · {pos.data.failingTerminals.length}
                  </div>
                  {pos.data.failingTerminals.length === 0 ? (
                    <p className="flex h-8 items-center gap-2 border-t border-[var(--color-border)] px-3 text-[12px] text-[var(--color-muted)]">
                      <Dot tone="ok" />
                      No failing terminals.
                    </p>
                  ) : (
                    <ul className="max-h-[216px] overflow-auto">
                      {pos.data.failingTerminals.map((t) => (
                        <li
                          key={t.terminalId}
                          className="flex h-7 items-center gap-2 border-t border-[var(--color-border)] px-3 text-[12px]"
                        >
                          <Dot tone="bad" />
                          <span className="shrink-0" style={mono}>
                            {t.locationCode ? (
                              <SLink to={loc(t.locationCode)} className="text-[var(--color-accent)] hover:underline">
                                {t.locationCode}
                              </SLink>
                            ) : (
                              '-'
                            )}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-[var(--color-muted)]" style={mono}>
                            {t.model} {t.serialNumber}
                          </span>
                          <span className="shrink-0" style={{ ...mono, color: VAR.offline }}>
                            {t.reason === 'not-checking-in' ? 'no check-in' : 'idle 24h'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </Panel>
          </div>
        </div>
      </div>
    </div>
  )
}
